import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Alert } from "@mui/material";
import { useRefreshTimer, useSpecificLocationData } from "../../hooks";
import {
  SelectedLocationForMove,
  Location,
  SpecialLocation,
} from "../../models";
import {
  LocationSection,
  LabDataSection,
} from "./move-billets-modal/components";
import {
  getLabData,
  upsertHeatComment,
} from "../../repository/BilletYardInventoryRepository";
import { PhysicsData, ChemistryData } from "../../models/coreModels";
import { NotificationSnackBar } from "../../../../controls/snackbars/notification-snackbar";

// Utility function to get location display text
const getLocationDisplayText = (location: Location | SpecialLocation) => {
  return location.HeaderText;
};

interface InfoLocationModalProps {
  show: boolean;
  onHide: () => void;
  locationId?: number | null;
  refreshInterval?: number;
}

export const InfoLocationModal = ({
  show,
  onHide,
  locationId,
  refreshInterval = 5,
}: InfoLocationModalProps) => {
  // State for selected heat
  const [selectedHeatIndex, setSelectedHeatIndex] = useState<number | null>(
    null,
  );
  const [physicsData, setPhysicsData] = useState<PhysicsData[]>([]);
  const [chemistryData, setChemistryData] = useState<ChemistryData[]>([]);
  const [isLabDataLoading, setIsLabDataLoading] = useState(false);
  const [notification, setNotification] = useState<{
    message: string | null;
    severity: "success" | "error";
  }>({ message: null, severity: "success" });

  // Hooks
  const {
    location: currentLocation,
    groups: currentGroups,
    isLoading,
    hasError,
    errorMessage,
    refreshLocationData,
  } = useSpecificLocationData({ locationId });

  useRefreshTimer(refreshInterval, refreshLocationData);

  // Fetch lab data (physics and chemistry) for a heat
  const fetchLabData = async (heatNumber: string) => {
    if (!heatNumber) {
      setPhysicsData([]);
      setChemistryData([]);
      return;
    }
    setIsLabDataLoading(true);
    const data = await getLabData(heatNumber);
    setPhysicsData(data.physics);
    setChemistryData(data.chemistry);
    setIsLabDataLoading(false);
  };

  // Handle heat selection from the left panel
  const handleHeatSelect = useCallback(
    (globalIndex: number) => {
      setSelectedHeatIndex((prevIndex) =>
        prevIndex === globalIndex ? null : globalIndex,
      );
      // Fetch lab data for the selected group's heat
      const selectedGroup = currentGroups?.[globalIndex];
      if (selectedGroup?.HeatNumber) {
        fetchLabData(selectedGroup.HeatNumber);
      }
    },
    [currentGroups],
  );

  // Save the heat-level comment, then refresh so the icon/tooltip/tiles update
  const handleSaveComment = useCallback(
    async (heatNumber: string, commentText: string, billetsGroupId: number) => {
      try {
        const resp = await upsertHeatComment(
          heatNumber,
          commentText,
          billetsGroupId,
        );
        if (resp?.ok) {
          const responseRow = resp.data?.tables?.[0]?.rows?.[0];
          const responseId = responseRow?.ResponseID;
          if (responseId === 0 || responseId == null) {
            await refreshLocationData();
          } else {
            setNotification({
              message: responseRow?.Message || "Failed to save comment",
              severity: "error",
            });
          }
        } else {
          setNotification({
            message: resp?.message || "Failed to save comment",
            severity: "error",
          });
        }
      } catch {
        setNotification({
          message: "Failed to save comment",
          severity: "error",
        });
      }
    },
    [refreshLocationData],
  );

  // Prepare location data for LocationSection component
  const prepareLocationData = (): SelectedLocationForMove | null => {
    if (!currentLocation || !currentGroups || currentGroups.length === 0) {
      return null;
    }

    return {
      location: currentLocation,
      groups: currentGroups,
      selectedGroupIndex: selectedHeatIndex,
      selectedBilletsCount: 0,
      role: null,
    };
  };

  // Reset form state when modal shows/closes
  useEffect(() => {
    // Reset selected heat when modal opens or location changes
    setSelectedHeatIndex(null);
    setPhysicsData([]);
    setChemistryData([]);
  }, [show, locationId]);

  // Auto-select first group and fetch lab data when groups change
  useEffect(() => {
    if (currentGroups && currentGroups.length > 0) {
      setSelectedHeatIndex((prevIndex) => {
        // Check if current selection is still valid
        if (prevIndex !== null) {
          const selectedGroup = currentGroups[prevIndex];
          if (!selectedGroup) {
            // Selected group no longer exists, reset to first group
            if (currentGroups[0]?.HeatNumber) {
              fetchLabData(currentGroups[0].HeatNumber);
            }
            return 0;
          }
          return prevIndex; // Keep current selection if group still exists
        } else {
          // No selection exists, auto-select first group
          if (currentGroups[0]?.HeatNumber) {
            fetchLabData(currentGroups[0].HeatNumber);
          }
          return 0;
        }
      });
    } else {
      setSelectedHeatIndex(null);
      setPhysicsData([]);
      setChemistryData([]);
    }
  }, [currentGroups]);

  const handleClose = (
    _event: {},
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onHide();
  };

  // Get selected heat for display
  const selectedHeat =
    selectedHeatIndex !== null ? currentGroups?.[selectedHeatIndex] : null;

  return (
    <Dialog open={show} onClose={handleClose} fullScreen disableEscapeKeyDown>
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          padding: (theme) => theme.spacing(1.5, 3),
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: "bold",
            color: (theme) => theme.palette.primary.contrastText,
            textAlign: "center",
            fontSize: "20px",
          }}
        >
          Location Info
          {currentLocation && ` - ${currentLocation.HeaderText}`}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          height: "calc(100vh - 120px)",
          padding: (theme) => theme.spacing(2),
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {hasError ? (
          <Alert
            severity="error"
            sx={{
              marginBottom: (theme) => theme.spacing(2),
              flexShrink: 0,
            }}
          >
            Failed to load location data: {errorMessage}
          </Alert>
        ) : isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
            }}
          >
            <CircularProgress size={60} />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ marginTop: "1rem" }}
            >
              Loading location data...
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "visible",
              minHeight: 0,
            }}
          >
            {/* Two-column layout */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flex: 1,
                width: "100%",
                marginTop: 2,
                minHeight: 0,
              }}
            >
              {/* Left Column - Location Display */}
              <Box
                sx={{
                  width: "33.33%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                    padding: "6px",
                    borderRadius: "5px 5px 0 0",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "16px",
                    flexShrink: 0,
                    marginBottom: "16px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "white",
                      margin: 0,
                    }}
                  >
                    {currentLocation
                      ? getLocationDisplayText(currentLocation)
                      : "Location"}
                  </Typography>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    background: (theme) =>
                      `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    padding: (theme) => theme.spacing(2),
                    boxShadow: (theme) => theme.shadows[2],
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "auto",
                  }}
                >
                  <LocationSection
                    location={prepareLocationData()}
                    fallbackLocation={currentLocation}
                    getLocationDisplayText={getLocationDisplayText}
                    showEmptyState={true}
                    onGroupSelect={handleHeatSelect}
                  />
                </Paper>
              </Box>

              {/* Right Column - Heat Information */}
              <Box
                sx={{
                  width: "66.67%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <LabDataSection
                  heat={selectedHeat || null}
                  physicsData={physicsData}
                  chemistryData={chemistryData}
                  variant="full"
                  isLoading={isLabDataLoading}
                  comment={selectedHeat?.Comments ?? null}
                  onSaveComment={handleSaveComment}
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: (theme) => theme.spacing(2),
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Close Button - Centered */}
        <Button
          onClick={onHide}
          variant="contained"
          size="large"
          sx={{
            minWidth: "300px",
            minHeight: "64px",
            fontSize: "20px",
            fontWeight: "bold",
            padding: (theme) => theme.spacing(2, 6),
            borderRadius: (theme) => theme.shape.borderRadius,
            backgroundColor: (theme) => theme.palette.grey[100],
            color: (theme) => theme.palette.text.primary,
            border: (theme) => `1px solid ${theme.palette.grey[300]}`,
            boxShadow: (theme) => theme.shadows[4],
            transition: "all 0.2s ease",
          }}
        >
          Close
        </Button>
      </DialogActions>

      <NotificationSnackBar
        message={notification.message}
        onHide={() => setNotification((n) => ({ ...n, message: null }))}
        severity={notification.severity}
      />
    </Dialog>
  );
};
